import React, { useState, useEffect, useRef, PropsWithChildren, forwardRef } from 'react'
import { CSVLink } from "react-csv";
import { handleDeNorm, normalizePhone, deNormalizePhone, handleNorm } from '../../utils/functions/tools/normalizePhone';
import {
    DataTable,
    Table,
    Link,
    Button,
    Pagination,
    TableBatchAction,
    TableBatchActions,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
    TableToolbar,
    TableToolbarAction,
    TableToolbarContent,
    TableToolbarSearch,
    TableToolbarMenu,
    Tag,
    Modal,
    TextInput,
    Select,
    SelectItem,
    Form,
    DatePicker,
    DatePickerInput,
    OverflowMenu,
    OverflowMenuItem,
    InlineNotification,
    NotificationActionButton
} from 'carbon-components-react';
import {
    Delete16 as Delete,
    Save16 as Save,
    Download16 as Download,
    Edit32 as Edit,
} from '@carbon/icons-react';

const clientAddressesHeaders = [
    { header: "ID", key: "id" },
    { header: "Type", key: "type" },
    { header: "Action", key: "action" },
    { header: "Start Date", key: "startDate" },
    { header: "End Date", key: "endDate" },
    { header: "Address 1", key: "address1" },
    { header: "Address 2", key: "address2" },
    { header: "City", key: "city" },
    { header: "Province", key: "province" },
    { header: "Country", key: "country" },
    { header: "Postal Code", key: "postalcode" },
];

export default {
    title: 'Components/DataTable/Batch Actions',
    component: DataTable,
    subcomponents: {
        TableBatchAction,
        TableBatchActions,
        TableToolbar,
        TableToolbarAction,
        TableToolbarContent,
        TableToolbarSearch,
        TableToolbarMenu,
        Table,
        TableBody,
        TableCell,
        TableContainer,
        TableHead,
        TableHeader,
        TableRow,
    },
};

export const ClientAddresses = forwardRef(({ setAddressesToDel, addressesToDel, setOpenDeleteAddressModal, openDeleteAddressModal, deleteAddress, curDate, addAddressForm, addAddress, countries, rows, totalItems, currentPageSize, setCurrentPageSize, setFirstRowIndex, openAddAddressModal, setOpenAddAddressModal, openEditAddressModal, setOpenEditAddressModal, editAddressRecord, editAddressSetup, editAddress, editAddressForm, editError, setEditError }: any, ref) => (
    <>
        <DataTable rows={rows} headers={clientAddressesHeaders} isSortable>
            {({
                rows,
                headers,
                getHeaderProps,
                getRowProps,
                getToolbarProps,
                getBatchActionProps,
                getTableProps,
                getTableContainerProps,
            }) => {
                const batchActionProps = getBatchActionProps();
                return (
                    <TableContainer
                        className="mt-1"
                        {...getTableContainerProps()}>
                        <TableToolbar {...getToolbarProps()}>
                            <TableToolbarContent
                                aria-hidden={batchActionProps.shouldShowBatchActions}>
                                <Button
                                    tabIndex={batchActionProps.shouldShowBatchActions ? -1 : 0}
                                    size="small"
                                    kind="primary"
                                    onClick={() => setOpenAddAddressModal(true)}>
                                    Add new
                                </Button>
                            </TableToolbarContent>
                        </TableToolbar>
                        <Table {...getTableProps()}>
                            <TableHead>
                                <TableRow>
                                    {headers.map((header, i) => (
                                        <TableHeader key={i} {...getHeaderProps({ header })}>
                                            {header.header}
                                        </TableHeader>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((row, i) => (
                                    <TableRow key={i} {...getRowProps({ row })}>
                                        {row.cells.map((cell) => (
                                            <TableCell key={cell.id}>
                                                {cell.info.header === "address1" ? <p>{cell.value}</p>
                                                    : cell.info.header === "action" ? <OverflowMenu renderIcon={Edit} className="m-auto">
                                                        <OverflowMenuItem onClick={() => setOpenEditAddressModal(true) & editAddressSetup(row.id)} itemText="Edit" />
                                                        <OverflowMenuItem onClick={() => setAddressesToDel(row.id) & setOpenDeleteAddressModal(true)} hasDivider isDelete itemText="Delete" />
                                                    </OverflowMenu>
                                                        : cell.info.header === "type" ? <p>{cell.value.type}</p>
                                                            : cell.info.header === "email" ? <Link href={`mailto:${cell.value}`}>{cell.value}</Link>
                                                                : cell.info.header === "status" ? cell.value === "Active" ? <Tag type="green" title="Clear Filter">{cell.value}</Tag>
                                                                    : <Tag type="red" title="Clear Filter">{cell.value}</Tag> : cell.value}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <Pagination
                            totalItems={totalItems}
                            pageSizes={[10, 20, 30, 40, 50]}
                            pageSize={currentPageSize}
                            onChange={({ page, pageSize }) => {
                                if (pageSize !== currentPageSize) {
                                    setCurrentPageSize(pageSize);
                                }
                                setFirstRowIndex(pageSize * (page - 1));
                            }}
                        />
                    </TableContainer>
                );
            }}
        </DataTable>

        <Modal
            open={openAddAddressModal}
            modalHeading="Add a new address"
            modalLabel="Client Addresses"
            primaryButtonText="Add"
            secondaryButtonText="Cancel"
            hasForm={true}
            onRequestClose={() => setOpenAddAddressModal(false) & setEditError(false)}
            onRequestSubmit={() => addAddressForm.current && addAddressForm.current.click() & setEditError(false)}
        >   {editError && <InlineNotification kind="error" hideCloseButton={true} title="Error: Unable to add client address" subtitle="Please try again or contact your administrator." actions={<NotificationActionButton onClick={(() => setEditError(false))}>Dismiss</NotificationActionButton>} />}
            <p style={{ marginBottom: '1rem' }}>
                Fill out the form to add a new address. You may add a new Business, Mailing, or Billing address.
            </p>
            <Form onSubmit={(e) => addAddress(e)} className="d-flex flex-w">
                <div className="col-12 col-md-6 m-005">
                    <DatePicker datePickerType="single" dateFormat="Y-m-d" style={{ marginBottom: '1rem' }}>
                        <DatePickerInput
                            required
                            id="startDate"
                            name="startDate"
                            placeholder="mm-dd-yyyy"
                            pattern="[0-9]{4}-(0[1-9]|1[012])-(0[1-9]|1[0-9]|2[0-9]|3[01])"
                            defaultValue={curDate}
                            labelText="Start date"
                        />
                    </DatePicker>
                </div>
                <div className="col-12 col-md-6 m-005">
                    <DatePicker datePickerType="single" dateFormat="Y-m-d" style={{ marginBottom: '1rem' }}>
                        <DatePickerInput
                            id="endDate"
                            name="endDate"
                            placeholder="mm-dd-yyyy"
                            labelText="End date"
                            pattern="[0-9]{4}-(0[1-9]|1[012])-(0[1-9]|1[0-9]|2[0-9]|3[01])"
                        />
                    </DatePicker>
                </div>
                <div className="col-12 m-005">
                    <Select id="type" name="type" defaultValue="Business" labelText="Address Type" style={{ marginBottom: '1rem' }}>
                        <SelectItem value="1" text="Business" />
                        <SelectItem value="2" text="Mailing" />
                        <SelectItem value="3" text="Billing" />
                    </Select>
                </div>
                <div className="col-12 col-md-6 m-005">
                    <TextInput
                        data-modal-primary-focus
                        id="address1"
                        name="address1"
                        invalidText="Address 1 is required"
                        labelText="Address 1"
                        placeholder="Enter address 1"
                        style={{ marginBottom: '1rem' }}
                        required
                    />
                </div>
                <div className="col-12 col-md-6 m-005">
                    <TextInput
                        data-modal-primary-focus
                        id="address2"
                        name="address2"
                        labelText="Address 2"
                        placeholder="Enter address 2"
                        style={{ marginBottom: '1rem' }}
                    />
                </div>
                <div className="col-12 col-md-6 m-005">
                    <TextInput
                        data-modal-primary-focus
                        id="city"
                        name="city"
                        labelText="City"
                        placeholder="Enter city"
                        style={{ marginBottom: '1rem' }}
                        required
                    />
                </div>
                <div className="col-12 col-md-6 m-005">
                    <TextInput
                        data-modal-primary-focus
                        id="province"
                        name="province"
                        labelText="Province/State"
                        placeholder="Enter province/state"
                        style={{ marginBottom: '1rem' }}
                        required
                    />
                </div>
                <div className="col-12 col-md-6 m-005">
                    <Select id="country" name="country" defaultValue="CA" labelText="Country">
                        {countries?.map((country, index) => (
                            <SelectItem key={index} value={country.countrycode} text={country.country} />
                        ))}
                    </Select>
                </div>
                <div className="col-12 col-md-6 m-005">
                    <TextInput
                        data-modal-primary-focus
                        id="postalcode"
                        name="postalcode"
                        labelText="Postal Code (optional)"
                        placeholder="Enter postal code"
                        style={{ marginBottom: '1rem' }}
                    />
                </div>
                <input type="submit" style={{ display: 'none' }} ref={addAddressForm} />
            </Form>
        </Modal>

        <Modal
            open={openEditAddressModal}
            modalHeading="Edit address"
            modalLabel="Client Addresses"
            primaryButtonText="Save Changes"
            secondaryButtonText="Cancel"
            hasForm={true}
            onRequestClose={() => setOpenEditAddressModal(false) & setEditError(false)}
            onRequestSubmit={() => editAddressForm.current && editAddressForm.current.click() & setEditError(false)}
        >   {editError && <InlineNotification kind="error" hideCloseButton={true} title="Error: Unable to edit client address" subtitle="Please try again or contact your administrator." actions={<NotificationActionButton onClick={(() => setEditError(false))}>Dismiss</NotificationActionButton>} />}
            <p style={{ marginBottom: '1rem' }}>
                Fill out the form to edit the selected address.
            </p>
            <Form onSubmit={(e) => editAddress(e)} className="d-flex flex-w">
                <div className="col-12 col-md-6 m-005">
                    <DatePicker datePickerType="single" dateFormat="Y-m-d" style={{ marginBottom: '1rem' }}>
                        <DatePickerInput
                            required
                            id="startDate"
                            name="startDate"
                            placeholder="yyyy-mm-dd"
                            pattern="[0-9]{4}-(0[1-9]|1[012])-(0[1-9]|1[0-9]|2[0-9]|3[01])"
                            defaultValue={editAddressRecord?.startDate}
                            labelText="Start date"
                        />
                    </DatePicker>
                </div>
                <div className="col-12 col-md-6 m-005">
                    <DatePicker datePickerType="single" dateFormat="Y-m-d" style={{ marginBottom: '1rem' }}>
                        <DatePickerInput
                            id="endDate"
                            name="endDate"
                            placeholder="yyyy-mm-dd"
                            defaultValue={editAddressRecord?.endDate}
                            labelText="End date"
                            pattern="[0-9]{4}-(0[1-9]|1[012])-(0[1-9]|1[0-9]|2[0-9]|3[01])"
                        />
                    </DatePicker>
                </div>
                <div className="col-12 m-005" style={{ marginBottom: '1rem' }}>
                    <Select id="type" name="type" labelText="Address Type">
                        <SelectItem selected={editAddressRecord?.type.type === 'Business' ? "selected" : ""} value="1" text="Business" />
                        <SelectItem selected={editAddressRecord?.type.type === 'Mailing' ? "selected" : ""} value="2" text="Mailing" />
                        <SelectItem selected={editAddressRecord?.type.type === 'Billing' ? "selected" : ""} value="3" text="Billing" />
                    </Select>
                </div>
                <div className="col-12 col-md-6 m-005">
                    <TextInput
                        data-modal-primary-focus
                        id="address1"
                        name="address1"
                        invalidText="Address 1 is required"
                        defaultValue={editAddressRecord?.address1}
                        labelText="Address 1"
                        placeholder="Enter address 1"
                        style={{ marginBottom: '1rem' }}
                        required
                    />
                </div>
                <div className="col-12 col-md-6 m-005">
                    <TextInput
                        data-modal-primary-focus
                        id="address2"
                        name="address2"
                        defaultValue={editAddressRecord?.address2}
                        labelText="Address 2"
                        placeholder="Enter address 2"
                        style={{ marginBottom: '1rem' }}
                    />
                </div>
                <div className="col-12 col-md-6 m-005">
                    <TextInput
                        data-modal-primary-focus
                        id="city"
                        name="city"
                        invalidText="City is required"
                        defaultValue={editAddressRecord?.city}
                        labelText="City"
                        placeholder="Enter city"
                        style={{ marginBottom: '1rem' }}
                        required
                    />
                </div>
                <div className="col-12 col-md-6 m-005">
                    <TextInput
                        data-modal-primary-focus
                        id="province"
                        name="province"
                        invalidText="Province is required"
                        defaultValue={editAddressRecord?.province}
                        labelText="Province/State"
                        placeholder="Enter province/state"
                        style={{ marginBottom: '1rem' }}
                        required
                    />
                </div>
                <div className="col-12 col-md-6 m-005">
                    <Select id="country" name="country" labelText="Country">
                        {countries?.map((country, index) => (
                            <SelectItem selected={editAddressRecord?.country === country.countrycode ? "selected" : ""} key={index} value={country.countrycode} text={country.country} />
                        ))}
                    </Select>
                </div>
                <div className="col-12 col-md-6 m-005">
                    <TextInput
                        data-modal-primary-focus
                        id="postalcode"
                        name="postalcode"
                        defaultValue={editAddressRecord?.postalcode}
                        labelText="Postal Code (optional)"
                        placeholder="Enter postal code"
                        style={{ marginBottom: '1rem' }}
                    />
                </div>
                <input type="submit" style={{ display: 'none' }} ref={editAddressForm} />
            </Form>
        </Modal>

        <Modal
            danger
            open={openDeleteAddressModal}
            modalHeading="Delete Client Address"
            modalLabel="Client Addresses"
            primaryButtonText="Delete"
            secondaryButtonText="Cancel"
            onRequestClose={() => setOpenDeleteAddressModal(false) & setEditError(false)}
            onRequestSubmit={() => deleteAddress(addressesToDel)}
        >
            {editError && <InlineNotification kind="error" hideCloseButton={true} title="Error: Unable to delete client address" subtitle="Please try again or contact your administrator." actions={<NotificationActionButton onClick={(() => setEditError(false))}>Dismiss</NotificationActionButton>} />}
            <p>Are you sure you want to delete the selected client address?</p>

        </Modal>
    </>
));